import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import ButtonIcon from '@glass/web/components/Button/ButtonIcon';
import Grid from '@glass/web/components/base/Unstable_Grid2';
import AddRounded from '@glass/web/components/base/icons/AddRounded';
import { selectIsUser } from '@glass/web/modules/auth';
import {
  NAME_FIELD_V2,
  RESUME_V2_FIELD,
  TEMPLATE_FIELD_V2,
} from '@glass/web/modules/resumes/fieldKeys';
import cardBreakpoints from '@glass/web/modules/theme/cardBreakpoints';

import ButtonPrimaryCta from '@glass/shared/components/Button/ButtonPrimaryCta';
import ResumeBuilderLink from '@glass/shared/components/Link/ResumeBuilderLink/ResumeBuilderLink';
import ResumeCard from '@glass/shared/components/Profile/ResumeCard';
import useResumeWords from '@glass/shared/modules/content/staticVariations/resumeWords/useResumeWords';
import useVerbWords from '@glass/shared/modules/content/staticVariations/verbWords/useVerbWords';
import useTemplateMetas from '@glass/shared/modules/resumes/hooks/useTemplateMetas';
import myResumesQuery from '@glass/shared/modules/resumes/queries/myResumes.graphql';

function UserResumes({ numLoading }) {
  const isUser = useSelector(selectIsUser);
  const resumeWords = useResumeWords();
  const verbWords = useVerbWords();

  const { data, loading: resumesLoading } = useQuery(myResumesQuery, {
    variables: { orderBy: 'updatedAt_DESC' },
    fetchPolicy: 'cache-and-network',
    ssr: false,
    skip: !isUser,
  });
  const { templateMetas, loading: templateMetasLoading } = useTemplateMetas();

  const templateMetaHash = useMemo(
    () =>
      templateMetas?.reduce(
        (agg, templateMeta) => ({
          ...agg,
          [templateMeta.slug]: templateMeta,
        }),
        null,
      ) || null,
    [templateMetas],
  );

  if (data?.me?.resumes?.length > 0 && templateMetaHash) {
    return (
      <Grid container spacing={1}>
        {data.me.resumes.map((resume) => {
          const templateSlug = resume[RESUME_V2_FIELD]?.[TEMPLATE_FIELD_V2]?.[NAME_FIELD_V2];
          const templateMeta =
            templateMetaHash[templateSlug] || templateMetaHash[templateMetas?.[0]?.slug];

          return (
            <Grid
              key={resume.id}
              lg={cardBreakpoints.lg}
              md={cardBreakpoints.md}
              xl={cardBreakpoints.xl}
              xs={cardBreakpoints.xs}
            >
              <ResumeCard resume={resume} templateMeta={templateMeta} />
            </Grid>
          );
        })}
      </Grid>
    );
  }

  if (!isUser || resumesLoading || templateMetasLoading) {
    return (
      <Grid container spacing={1}>
        {Array.from({ length: numLoading }, (_, idx) => (
          <Grid
            key={idx}
            lg={cardBreakpoints.lg}
            md={cardBreakpoints.md}
            xl={cardBreakpoints.xl}
            xs={cardBreakpoints.xs}
          >
            <ResumeCard key={idx} loading prefetch={idx === 0} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={1}>
      <Grid xs={12}>
        <ButtonPrimaryCta component={ResumeBuilderLink}>
          <ButtonIcon icon={AddRounded} />
          {verbWords.capitalized} a {resumeWords.abbreviatedCapitalized}
        </ButtonPrimaryCta>
      </Grid>
    </Grid>
  );
}

UserResumes.defaultProps = {
  numLoading: 1,
};

UserResumes.propTypes = {
  numLoading: PropTypes.number,
};

export default React.memo(UserResumes);
